import { useEffect, useMemo, useState } from 'react';
import { handoffContactList, listContactProjectSummaries, listContactRecords } from '../api/contactBuilder';
import {
  ContactProjectSummary,
  formatShortDate,
  platformHandoffUrl,
  projectStatusLabel,
} from '../utils/contactBuilder';

type ContactBuilderOpsProps = {
  communityId?: string
  resourceId: string
  resource?: { partner_handoff?: { partner?: { hub_url?: string } } } | null
  initialProjectId?: string
};

type ContactRow = {
  id: string
  organization?: string
  contact_name?: string
  role?: string
  public_business_email?: string
  public_business_phone?: string
  city?: string
  state_or_region?: string
  record_status?: string
  score?: number
  source_url?: string
};

const COLUMNS: { key: keyof ContactRow; label: string }[] = [
  { key: 'organization', label: 'Organization' },
  { key: 'contact_name', label: 'Contact' },
  { key: 'role', label: 'Role' },
  { key: 'public_business_email', label: 'Email' },
  { key: 'public_business_phone', label: 'Phone' },
  { key: 'city', label: 'City' },
  { key: 'state_or_region', label: 'State' },
  { key: 'record_status', label: 'Status' },
  { key: 'score', label: 'Score' },
  { key: 'source_url', label: 'Source' },
];

const csvCell = (value: unknown) => {
  const text = value === undefined || value === null ? '' : String(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const contactRowsToCsv = (rows: ContactRow[]) => [
  COLUMNS.map((column) => column.label).join(','),
  ...rows.map((row) => COLUMNS.map((column) => csvCell(row[column.key])).join(',')),
].join('\n');

const downloadCsv = (rows: ContactRow[], name: string) => {
  const url = URL.createObjectURL(new Blob([contactRowsToCsv(rows)], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `${(name || 'contacts').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'contacts'}-contacts.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const STATUS_TONE: Record<string, string> = {
  'Handed off': 'bg-[#F1ECFB] text-[#3B2A63]',
  Disconnected: 'bg-[#EEF2F4] text-[#4D4D4D]',
  'In progress': 'bg-[#EAF1FB] text-[#2A5DA8]',
};

/**
 * Staff view of member contact lists for a DSCVRY expert.
 * Lists are generated by members on the pantry. Staff review the rows and hand a list to DSCVRY.
 */
const ContactBuilderOps = ({ communityId, resourceId, resource, initialProjectId = '' }: ContactBuilderOpsProps) => {
  const [projects, setProjects] = useState<ContactProjectSummary[]>([]);
  const [selectedId, setSelectedId] = useState(initialProjectId);
  const [rows, setRows] = useState<ContactRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [openUrl, setOpenUrl] = useState('');

  useEffect(() => {
    if (!communityId || !resourceId) return;
    let cancelled = false;
    setLoading(true);
    listContactProjectSummaries(communityId, resourceId)
      .then((items: ContactProjectSummary[]) => {
        if (cancelled) return;
        setProjects(items);
        setSelectedId((current) => current || items.find((item) => item.record_count > 0)?.project_id || '');
      })
      .catch(() => { if (!cancelled) setError('Member lists could not be loaded.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [communityId, resourceId]);

  useEffect(() => {
    if (initialProjectId) setSelectedId(initialProjectId);
  }, [initialProjectId]);

  useEffect(() => {
    if (!communityId || !selectedId) {
      setRows([]);
      return;
    }
    let cancelled = false;
    setOpenUrl('');
    listContactRecords(communityId, selectedId)
      .then((items: ContactRow[]) => { if (!cancelled) setRows(items); })
      .catch(() => { if (!cancelled) setRows([]); });
    return () => { cancelled = true; };
  }, [communityId, selectedId]);

  const selected = useMemo(() => projects.find((item) => item.project_id === selectedId), [projects, selectedId]);

  const handoff = async () => {
    if (!communityId || !selected?.latest_brief_id) return;
    const tab = window.open('about:blank', '_blank');
    setBusy(true);
    setError('');
    try {
      const result = await handoffContactList(communityId, selected.latest_brief_id, resourceId);
      const target = platformHandoffUrl(resource, result?.open_url || '', selected.project_id);
      setOpenUrl(target);
      setProjects((items) => items.map((item) => (
        item.project_id === selected.project_id ? { ...item, handed_at: new Date().toISOString() } : item
      )));
      if (tab) {
        tab.opener = null;
        tab.location.href = target;
      } else {
        window.open(target, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      tab?.close();
      setError('This list is not ready to hand off.');
    } finally {
      setBusy(false);
    }
  };

  if (!communityId) {
    return <p className="mx-auto my-4 w-11/12 font-open-sans text-sm text-[#4D4D4D]">Open this resource from a community account.</p>;
  }

  return (
    <div className="mx-auto my-4 w-11/12 rounded-2xl border border-[#E4EEF1] bg-white p-6 font-open-sans text-sm" data-testid="contact-builder-ops">
      <p className="page-kicker">Contact research</p>
      <h2 className="mt-1 font-raleway text-lg font-semibold text-ink">Member contact lists</h2>
      <p className="mt-1 text-[#4D4D4D]">Lists are generated by members for their pantry business. Review the rows here and hand a list to DSCVRY.</p>

      <div className="mt-5 grid gap-4 md:grid-cols-[260px_1fr]">
        <ul className="space-y-1" data-testid="member-projects">
          {loading && <li className="text-[#4D4D4D]">Loading member lists.</li>}
          {!loading && projects.length === 0 && <li className="text-[#4D4D4D]">No member has generated a list yet.</li>}
          {projects.map((item) => {
            const status = projectStatusLabel(item);
            const active = item.project_id === selectedId;
            return (
              <li key={item.project_id}>
                <button
                  type="button"
                  onClick={() => setSelectedId(item.project_id)}
                  className={`w-full rounded-lg border px-3 py-2 text-left ${active ? 'border-primaryDark-0 bg-[#F6FAFB]' : 'border-[#E4EEF1] hover:bg-[#F6FAFB]'}`}
                  data-testid={`member-project-${item.project_id}`}
                >
                  <span className="flex items-center justify-between gap-2">
                    <span className="truncate font-raleway font-semibold text-ink">{item.name || 'Contact research'}</span>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_TONE[status]}`}>{status}</span>
                  </span>
                  <span className="mt-0.5 block text-xs text-[#4D4D4D]">
                    {item.record_count} rows · {item.review_count} needs review · {formatShortDate(item.last_generated)}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        <div className="min-w-0 space-y-3">
          {selected ? (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="font-raleway font-semibold text-ink">{selected.name || 'Contact research'}</p>
                  <p className="text-xs text-[#4D4D4D]">
                    {selected.record_count} rows · {selected.active_count} active · {selected.review_count} needs review{selected.imported ? ' · DSCVRY steps imported' : ''}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button type="button" className="rounded-lg border border-[#E4EEF1] px-3 py-2 font-raleway disabled:opacity-50" disabled={rows.length === 0} onClick={() => downloadCsv(rows, selected.name || '')}>
                    Download CSV
                  </button>
                  <button
                    type="button"
                    className="rounded-lg bg-primaryDark-0 px-3 py-2 font-raleway text-white disabled:opacity-50"
                    disabled={busy || rows.length === 0 || !selected.latest_brief_id || selected.connection_status === 'revoked'}
                    onClick={handoff}
                    data-testid="ops-hand-to-dscvry"
                  >
                    {busy ? 'Handing off' : 'Hand list to DSCVRY'}
                  </button>
                </div>
              </div>
              {openUrl && (
                <p className="text-xs text-[#3B2A63]">
                  Handed to DSCVRY. <a href={openUrl} target="_blank" rel="noreferrer" className="font-semibold underline">Open DSCVRY</a>
                </p>
              )}
              <div className="max-h-[420px] overflow-auto rounded-lg border border-[#E4EEF1]">
                <table className="min-w-full border-collapse text-left text-xs" data-testid="ops-contact-sheet">
                  <thead className="sticky top-0 bg-[#F6FAFB]">
                    <tr>
                      {COLUMNS.map((column) => (
                        <th key={column.key} className="whitespace-nowrap border-b border-[#E4EEF1] px-3 py-2 font-semibold text-ink">{column.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.id} className="odd:bg-white even:bg-[#FBFDFD]">
                        {COLUMNS.map((column) => {
                          const value = row[column.key];
                          if (column.key === 'source_url' && value) {
                            return (
                              <td key={column.key} className="max-w-[220px] truncate border-b border-[#E4EEF1] px-3 py-2">
                                <a href={String(value)} target="_blank" rel="noreferrer" className="text-primaryDark-0 underline">{String(value)}</a>
                              </td>
                            );
                          }
                          const text = column.key === 'record_status' && value === 'uncertain' ? 'needs review' : value;
                          return <td key={column.key} className="max-w-[220px] truncate border-b border-[#E4EEF1] px-3 py-2">{text === undefined || text === '' ? '—' : String(text)}</td>;
                        })}
                      </tr>
                    ))}
                    {rows.length === 0 && (
                      <tr><td className="px-3 py-3 text-[#4D4D4D]" colSpan={COLUMNS.length}>This list has no rows yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            !loading && projects.length > 0 && <p className="text-[#4D4D4D]">Select a member list to review its rows.</p>
          )}
          {error && <p className="text-[#B91C1C]">{error}</p>}
        </div>
      </div>
    </div>
  );
};

export default ContactBuilderOps;
